import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Row, Col, Card, Form, Button, Offcanvas } from 'react-bootstrap';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { LuSettings2 } from "react-icons/lu";
import PriceRangeFilter from '../components/PriceRangeFilter';
import defaultProductImage from '../images/default-product.png';
import './CatalogPage.css';
import { fetchProductList } from '../store/actions';
import productService from '../services/productService';
import useTitle from '../hooks/useTitle';

const Catalog = () => {
    // Состояния для фильтров
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
    const [priceRangeLoading, setPriceRangeLoading] = useState(false);
    // На телефоне и планшете на одной странице 10 товаров, на ПК - 9
    const [pageSize, setPageSize] = useState(window.innerWidth >= 992 ? 9 : 10);
    // Данные из Redux
    const { data: categories, loading: categoriesLoading, error: categoriesError } = useSelector(state => state.categories.all);
    const { data: { items: products, totalItems: totalProducts, currentPage, totalPages }, loading: productsLoading, error: productsError } = useSelector(state => state.productList);
    // Отображение фильтров в мобильной версии
    const [showMobileFilters, setShowMobileFilters] = useState(false); 
   
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // Извлечение названия категории из пути
    const params = useParams(); 
    const categoryParam = params.categoryName;

    // Извлечение параметров запроса
    const queryParams = new URLSearchParams(location.search); 
    const searchParam = queryParams.get('search');
    const categoriesParam = queryParams.get('categories');
    const minPriceParam = queryParams.get('minPrice');
    const maxPriceParam = queryParams.get('maxPrice');
    const pageParam = parseInt(queryParams.get('page')) || 1;

    // Значения фильтра цены
    const [selectedPriceRange, setSelectedPriceRange] = useState({ 
        min: minPriceParam ? Number(minPriceParam) : 0, 
        max: maxPriceParam ? Number(maxPriceParam) : 0 
    });

    // Задаем название сайта
    useTitle(categories.find(cat => cat.name === categoryParam) ? `Каталог | ${categories.find(cat => cat.name === categoryParam).title}` : 'Каталог');
    
    // Определение текущего режима и настройка хлебных крошек
    const getPageMode = () => {
        if (searchParam) {
            return {
                mode: 'search',
                breadcrumbs: (
                    <div className="breadcrumbs mb-3">
                        <Link to="/">Главная</Link> {' > '} <Link to="/catalog">Каталог</Link> {' > '} <span>Результаты поиска</span>
                    </div>
                ),
                title: 'Результаты поиска' // `Результаты поиска: "${searchParam}"`  TODO м.б. позже заменить на "Найдено Х товаров в Y категориях"
            };
        } else if (categoryParam) {
            const category = categories.find(cat => cat.name === categoryParam);
            if (!category && !categoriesLoading) {
                // dispatch(showNotification('Категория не найдена', 'error'));
                navigate('/catalog');

                // Возвращаем дефолтный режим для корректной работы до редиректа
                return {
                    mode: 'all',
                    breadcrumbs: (
                        <div className="breadcrumbs mb-3">
                            <Link to="/">Главная</Link> {' > '} <span>Каталог</span>
                        </div>
                    ),
                    title: 'Категория не найдена'
                };
            }
            else {
                return {
                    mode: 'category',
                    breadcrumbs: (
                        <div className="breadcrumbs mb-3">
                            <Link to="/">Главная</Link> {' > '} <Link to="/catalog">Каталог</Link> {' > '} 
                            <span>{category ? category.title : 'Загрузка...'}</span>
                        </div>
                    ),
                    title: 'Каталог'
                };
            }
        } else if (categoriesParam) {
            return {
                mode: 'multiCategory',
                breadcrumbs: (
                    <div className="breadcrumbs mb-3">
                        <Link to="/">Главная</Link> {' > '} <span>Каталог</span>
                    </div>
                ),
                title: 'Каталог'
            };
        } else {
            return {
                mode: 'all',
                breadcrumbs: (
                    <div className="breadcrumbs mb-3">
                        <Link to="/">Главная</Link> {' > '} <span>Каталог</span>
                    </div>
                ),
                title: 'Каталог'
            };
        }
    };
    
    const { mode, breadcrumbs, title } = getPageMode();

    const fetchProducts = () => {
        // Формирование параметров запроса
        let prms = {};
        
        if (searchParam) {
            prms.q = searchParam;
        }
        if (categoryParam) {
            prms.categories = categoryParam;
        }
        else if (categoriesParam) {
            prms.categories = categoriesParam;
        }
        if (minPriceParam) {
            prms.minPrice = minPriceParam;
        }
        if (maxPriceParam) {
            prms.maxPrice = maxPriceParam;
        }

        prms.currentPage = pageParam;
        prms.pageSize = pageSize;

        // Отправляем запрос к API
        dispatch(fetchProductList(prms));
    };

    // Эффект для изменения pageSize при изменении размера экрана
    useEffect(() => {
        const handleResize = () => {
            const newPageSize = window.innerWidth >= 992 ? 9 : 10;
            if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [pageSize]);

    // Загрузка товаров при изменении параметров
    useEffect(() => {
        fetchProducts();
    }, [mode, searchParam, categoryParam, categoriesParam, minPriceParam, maxPriceParam, pageParam, pageSize]);
    
    // Функция для перехода на определенную страницу
    const handlePageChange = (pageNumber) => {
        // Создаем новый объект URLSearchParams на основе текущего URL
        const newQueryParams = new URLSearchParams(location.search);
        
        // Обновляем параметр страницы
        newQueryParams.set('page', pageNumber);
        
        // Формируем новый URL
        let newUrl = location.pathname;
        if (newQueryParams.toString()) {
            newUrl += '?' + newQueryParams.toString();
        }
        
        // Переходим по новому URL
        navigate(newUrl);
    };

    // Установка выбранных категорий, исходя из параметров запроса
    useEffect(() => {
        if (categoriesParam && categories.length > 0) {
            const catArray = categoriesParam.split(',');
            setSelectedCategories(catArray);
        } else if (categoryParam && categories.length > 0) {
            setSelectedCategories([categoryParam]);
        } else {
            setSelectedCategories([]);
        }
    }, [categoryParam, categoriesParam, categories]);

    // Эффект для получения диапазона цен товаров на основе выбранных категорий и строки поиска
    useEffect(() => {
        let loadingTimer;
        
        const loadPriceRange = async () => {
            // Устанавливаем таймер для отображения состояния загрузки через 500мс
            // Это предотвратит мерцание индикатора при быстрых запросах
            loadingTimer = setTimeout(() => {
                setPriceRangeLoading(true);
            }, 500);
            
            // Формируем параметры для учета текущей категории или поиска
            let params = {};

            // Если категории выбраны в интерфейсе, но еще не применены (не попали в URL)
            const selectedCategoriesString = selectedCategories.join(',');
            if (selectedCategories.length > 0 && 
                selectedCategoriesString !== categoryParam && 
                selectedCategoriesString !==categoriesParam) {
                params.categories = selectedCategoriesString;
            }
            else {
                if (categoryParam) {
                    params.categories = categoryParam;
                } else if (categoriesParam) {
                    params.categories = categoriesParam;
                }
                if (searchParam) {
                    params.q = searchParam;
                }
            }
            
            const result = await productService.getPriceRange(params);
            
            // Очищаем таймер загрузки, чтобы не показывать индикатор, 
            // если запрос завершился быстро
            clearTimeout(loadingTimer);
            
            if (result.success) {
                // Проверяем, есть ли товары с ценами в категории (оба значения не равны нулю)
                const hasPriceRange = result.data.minPrice !== 0 || result.data.maxPrice !== 0;
    
                if (hasPriceRange) {
                    let newPriceRange;
                    if (result.data.minPrice == result.data.maxPrice) {
                        newPriceRange = {
                            min: result.data.minPrice,
                            max: result.data.maxPrice + 1
                        };
                    }
                    else {
                        newPriceRange = {
                            min: result.data.minPrice,
                            max: result.data.maxPrice
                        };
                    }
                    
                    setPriceRange(newPriceRange);
    
                    setSelectedPriceRange(prev => {
                        // Если нет параметров — полностью сбрасываем
                        if (!minPriceParam && !maxPriceParam) {
                            return { min: newPriceRange.min, max: newPriceRange.max };
                        }

                        // Иначе строим новый диапазон от newPriceRange
                        let newMin = newPriceRange.min;
                        let newMax = newPriceRange.max;

                        if (minPriceParam) {
                            newMin = Math.max(newPriceRange.min, Number(minPriceParam));
                        }
                        if (maxPriceParam) {
                            newMax = Math.min(newPriceRange.max, Number(maxPriceParam));
                        }

                        // Проверка целостности диапазона
                        if (newMin > newMax) {
                            newMin = newPriceRange.min;
                            newMax = newPriceRange.max;
                        }

                        return { min: newMin, max: newMax };
                    });
                }
                else {
                    setPriceRange({ min: 0, max: 1 });
                    setSelectedPriceRange({ min: 0, max: 1 });
                }
            }
            
            // Устанавливаем состояние загрузки в false после завершения
            setPriceRangeLoading(false);
        };
        
        loadPriceRange();
        
        // Очистка таймера при размонтировании компонента
        return () => {
            clearTimeout(loadingTimer);
        };
    }, [categoryParam, categoriesParam, searchParam, minPriceParam, maxPriceParam, selectedCategories]);

    // Обработчик изменения выбранных категорий
    const handleCategoryChange = (e) => {
        const category = e.target.value;
        
        if (e.target.checked) {
            setSelectedCategories([...selectedCategories, category]);
        } else {
            setSelectedCategories(selectedCategories.filter(cat => cat !== category));
        }
    };

    // Обработчик изменения диапазона цен
    const handlePriceChange = (min, max) => {
        setSelectedPriceRange({ min, max });
    };
    
    // Обработчик применения фильтров
    const applyFilters = () => {
        let url = '/catalog';

        if (selectedCategories.length === 1) {
            url += `/${selectedCategories[0]}`;
        }

        // Создаем новый объект URLSearchParams
        const newParams = new URLSearchParams();

        // Если выбрано несколько категорий
        if (selectedCategories.length > 1) {
            newParams.set('categories', selectedCategories.join(','));
        }

        // Добавляем параметры цены
        if (selectedPriceRange.min > priceRange.min) {
            newParams.set('minPrice', selectedPriceRange.min);
        }
        
        if (selectedPriceRange.max < priceRange.max) {
            newParams.set('maxPrice', selectedPriceRange.max);
        }

        // Добавляем параметр поиска, если он есть
        if (searchParam) {
            newParams.set('search', searchParam);
        }

        // Добавляем параметры в URL, если они есть
        if (newParams.toString()) {
            url += `?${newParams.toString()}`;
        }

        navigate(url);
        // Закрываем мобильные фильтры после применения
        setShowMobileFilters(false);
    };
    
    // Обработчик клика на товар
    const handleProductClick = (product) => {
        navigate(`/catalog/${product.category}/${product.name}-${product.id}`);
    }; 

    return (
        <Container className="catalog-page py-4">
            {/* Хлебные крошки */}
            {breadcrumbs}
             
            {/* Боковая панель с фильтрами для мобильных устройств */}
            <Offcanvas 
                show={showMobileFilters} 
                onHide={() => setShowMobileFilters(false)} 
                placement="end"
                className="mobile-filters-offcanvas"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Фильтры товаров</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {/* Фильтр по категориям */}
                    <div className="filter-section mb-4" style={{borderTop: 'none'}}>
                        <div className="mb-2 mt-2">
                            <h5 className="filter-section-title">Категории</h5>
                        </div>
                        <div className="categories-parent-container"> 
                            <div className="categories-container">
                                {categoriesLoading ? 
                                    <LoadingSpinner message="Загрузка категорий..." size="sm" /> :
                                    <Form>
                                        {categories.map(category => (
                                            <Form.Check 
                                                key={category.id}
                                                type="checkbox"
                                                id={`category-mobile-${category.id}`}
                                                label={category.title}
                                                value={category.name}
                                                checked={selectedCategories.includes(category.name)}
                                                onChange={handleCategoryChange}
                                                className="mb-2"
                                            />
                                        ))}
                                    </Form>
                                }
                            </div>
                        </div>
                    </div>
                    {/* Фильтр по цене */}
                    <div className="filter-section mb-4">
                        <PriceRangeFilter
                            initialRange={priceRange}
                            selectedRange={selectedPriceRange}
                            onPriceChange={handlePriceChange}
                            isLoading={priceRangeLoading}
                        />
                    </div>
                    {/* Кнопка применения фильтров */}
                    <Button 
                        variant="warning" 
                        className="w-100 text-white rounded-pill"
                        onClick={applyFilters}
                    >
                        Применить
                    </Button>
                </Offcanvas.Body>
            </Offcanvas>

            <Row>
                {/* Фильтры для десктопа*/}
                <Col lg={3} md={4} className="d-none d-md-block mb-3">
                    <div className="filters-container p-3 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className="filters-title mb-0">Фильтры</h3>
                            <LuSettings2 size={20} />
                        </div>
                        {/* Фильтр по категориям */}
                        <div className="filter-section mb-4">
                            <div className="mb-2 mt-3">
                                <h5 className="filter-section-title">Категории</h5>
                            </div>
                            <div className="categories-parent-container"> 
                                <div className="categories-container">
                                    {categoriesLoading ? 
                                        <LoadingSpinner message="Загрузка категорий..." size="sm" /> :
                                        <Form>
                                            {categories && categories.map(category => (
                                                <Form.Check 
                                                    key={category.id}
                                                    type="checkbox"
                                                    id={`category-${category.id}`}
                                                    label={category.title}
                                                    value={category.name}
                                                    checked={selectedCategories.includes(category.name)}
                                                    onChange={handleCategoryChange}
                                                    className="mb-2"
                                                />
                                            ))}
                                        </Form>
                                    }
                                </div>
                            </div>
                        </div>
                        {/* Фильтр по цене */}
                        <div className="filter-section mb-4">
                            <PriceRangeFilter
                                initialRange={priceRange}
                                selectedRange={selectedPriceRange}
                                onPriceChange={handlePriceChange}
                                isLoading={priceRangeLoading}
                            />
                        </div>
                        {/* Кнопка применения фильтров */}
                        <Button 
                            variant="warning" 
                            className="w-100 text-white rounded-pill"
                            onClick={applyFilters}
                        >
                            Применить
                        </Button>
                    </div>
                </Col>

                <Col lg={9} md={8} style={{marginBottom: "50px", minHeight: "80vh"}}>
                    {/* Заголовок и информация о количестве товаров */}
                    <Row className="mb-3 justify-content-between">
                        <Col xs={4} md={6}>
                            <h1 className="catalog-title">{title}</h1> 
                        </Col>
                        <Col xs={8} md={6} className="d-flex align-items-center justify-content-end">
                            {!productsLoading && totalProducts !== 0 &&
                                <div className="products-info">
                                    Показано {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalProducts)} из {totalProducts} товаров
                                </div>
                            }  
                            {/* Кнопка фильтров для мобильных устройств */}
                            <Button
                                className="d-flex d-md-none justify-content-between align-items-center filter-btn-mobile rounded-pill"
                                onClick={() => setShowMobileFilters(true)}
                            >
                                <LuSettings2 />
                            </Button>
                        </Col>
                    </Row>
                    {/* Список товаров */}
                    {productsLoading ? <LoadingSpinner message="Загрузка товаров..." containerHeight="vh-50"/>
                     :
                        <>
                            {products.length === 0 ? (
                                <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{minHeight: "50vh"}}>
                                    <h3>Товары не найдены</h3>
                                    <p>Попробуйте изменить параметры поиска или фильтрации</p>
                                </div>
                            ) : (
                                <>
                                    <Row>
                                        {products.map(product => (
                                            <Col key={product.id} lg={4} md={6} sm={6} xs={6} className="mb-4">
                                                <Card 
                                                    className="product-card h-100 border-0 shadow-sm"
                                                    onClick={() => handleProductClick(product)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <div className="product-image-container">
                                                        <Card.Img 
                                                            variant="top" 
                                                            src={product.image || defaultProductImage} 
                                                            alt={product.title}
                                                            className="product-image"
                                                            draggable="false"
                                                            onDragStart={(e) => e.preventDefault()}
                                                            onError={(e) => {
                                                                e.target.onerror = null; // Предотвращает зацикливание
                                                                e.target.src = defaultProductImage;
                                                            }}
                                                        />
                                                    </div>
                                                    <Card.Body className="text-center d-flex flex-column">
                                                        <Card.Title className="product-title">{product.title}</Card.Title>
                                                        {/* <div className="product-article text-muted mb-2">
                                                            Артикул: {product.article}
                                                        </div> */}
                                                        <div className="product-price fw-bold fs-5 mt-auto">
                                                            {product.price !== null ? `${product.price} ₽` : "Цена по запросу"}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>

                                    {/* Горизонтальная палочка */}
                                    <Row >
                                        <Col>
                                            <hr className="horizontal-line" />
                                        </Col>
                                    </Row>

                                    {totalPages > 1 && (
                                        <Pagination 
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    }
                </Col>    
            </Row>
        </Container>
    );
};

export default Catalog;